import { Observable, BehaviorSubject } from 'rxjs';
import { ReceiptPrinter } from '../classes/receipt-printer.class';
import { PrinterServiceInterface } from '@shared/interfaces/printer-service.interface';
import { PrinterDto } from '../dto/printer.dto';
import { ReceiptPrintersStore, ReceiptPrintersStoreActions } from '../receipt-printers.store';
import { PrinterType } from '../enum/printer-type.enum';
import { LogService } from '../../logger/log.service';
import { ignoreImageProps } from '@core/services/system/logger';
import { PermissionService } from '../../permission/permission.service';
import { PlatformService } from '../../platform/platform.service';
import { PermissionTypes } from '@shared/constants/permission-types.enum';
import { Injector } from '@angular/core';
import { ErrorLevel, ILogger } from '@paymash/logging';

export type OnDiscover = (data: unknown) => void;
export type OnError = (error: unknown) => void;
export type OnLogEvent = (data: { message: string }) => void;

export abstract class CommonPrinterService implements PrinterServiceInterface {
  protected abstract get pluginName(): string;

  protected abstract get discoverMethodName(): string;

  protected abstract get stopDiscoverMethodName(): string;

  protected abstract get printMethodName(): string;

  protected isAndroid: boolean;
  protected scanningInterval: number = 30000;
  protected platformService: PlatformService;
  protected permissionService: PermissionService;
  protected logger: ILogger;

  private scanningAbortTaskNumber = null;
  private _discoverInProgress: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private hasPermissionOnAndroid = false;
  private commonLogger: ILogger;
  // J5: while a print burst is in progress, PrinterService suppresses discovery so a
  // concurrent LAN scan does not contend with the print connection (getPort). Idle discovery
  // still runs between prints so printer IPs stay fresh after DHCP changes.
  private discoverySuppressed = false;

  constructor(protected injector: Injector) {
    this.platformService = injector.get(PlatformService);
    this.permissionService = injector.get(PermissionService);
    const logService = injector.get(LogService);
    this.commonLogger = logService.createLogger('CommonPrinterService');
    this.initLogger(logService);
    this.logsSubscription();
    this.isAndroid = this.platformService.isAndroid;
  }

  private initLogger(logService: LogService) {
    this.logger = logService.createLogger(this.pluginName);
  }

  protected stopDiscoverAndClearInterval() {
    const clearTask = () => {
      clearTimeout(this.scanningAbortTaskNumber);
      this.scanningAbortTaskNumber = null;
      this._discoverInProgress.next(false);
    };

    this.stopDiscover()
      .then(() => clearTask())
      .catch(() => clearTask());
  }

  protected _printersList: ReceiptPrintersStore = new ReceiptPrintersStore([]);

  protected discover(): Observable<ReceiptPrinter[]> {
    this.commonLogger.debug('pluginName:discover', { pluginName: this.pluginName });
    this._printersList.dispatch({
      type: ReceiptPrintersStoreActions.CLEAR_STORE,
    });
    this.callDiscoverMethod(
      (data) => {
        this.handleDiscover(data);
      },
      (error) => {
        this.commonLogger.error(error, 'pluginName:discover', { level: ErrorLevel.Medium }, { pluginName: this.pluginName });
      }
    );
    return this.printerList;
  }

  protected callDiscoverMethod(onDiscover: OnDiscover, onError: OnError) {
    window.cordova.exec(
      (data) => onDiscover(data),
      (err) => onError(err),
      this.pluginName,
      this.discoverMethodName,
      []
    );
  }

  protected handleDiscover(printers: any) {
    if (printers instanceof Array) {
      this._printersList.dispatch({
        type: ReceiptPrintersStoreActions.ADD_PRINTERS,
        payload: printers.map((printer) => new ReceiptPrinter(printer)),
      });
    }
  }

  protected stopDiscover(): Promise<void> {
    this.commonLogger.debug('pluginName:stopDiscover', { pluginName: this.pluginName });
    return this.callStopDiscoverMethod();
  }

  protected callStopDiscoverMethod(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.cordova) {
        window.cordova.exec(resolve, reject, this.pluginName, this.stopDiscoverMethodName, []);
      } else {
        resolve();
      }
    });
  }

  public setDiscoverySuppressed(suppressed: boolean): void {
    this.discoverySuppressed = suppressed;
    // Stop a scan that may have just started while the printer list was resolved for this
    // print, so it stops contending with the print connection immediately.
    if (suppressed && this.scanningAbortTaskNumber) {
      this.stopDiscoverAndClearInterval();
    }
  }

  get printerList(): Observable<ReceiptPrinter[]> {
    if (!this.discoverySuppressed && !this.scanningAbortTaskNumber) {
      this.scanningAbortTaskNumber = setTimeout(() => this.stopDiscoverAndClearInterval(), this.scanningInterval);
      this.checkPermissionsOnAndroid()
        .then((hasPermission) => {
          if (hasPermission) {
            this._discoverInProgress.next(true);
            this.discover();
          }
        })
        .catch((err) => this.commonLogger.error(err, 'printerList:checkPermissionsOnAndroid'));
    }
    return this._printersList.asObservable();
  }

  public print(device: ReceiptPrinter, receipt: any[]): Promise<void> {
    this.commonLogger.debug('pluginName:print', { pluginName: this.pluginName });
    let printer: PrinterDto;
    switch (device.deviceType) {
      case PrinterType.Star:
        printer = new PrinterDto(device.deviceType, device.target, device.printerModel, device.ipAddress, device.macAddress.toLowerCase());
        break;
      default:
        printer = new PrinterDto(device.deviceType, device.target, device.printerModel, device.ipAddress, device.macAddress);
        break;
    }
    this.commonLogger.debug('pluginName:print:pluginRequestData: printer and receipt', {
      printer,
      receipt: ignoreImageProps(receipt),
    });
    return this.printReceipt(receipt, printer);
  }

  public async checkPermissionsOnAndroid(): Promise<boolean> {
    const isAndroid10AndMore: boolean = await this.platformService.isAndroid10AndMore();
    const isAndroid12AndMore: boolean = await this.platformService.isAndroid12AndMore();
    try {
      if (this.hasPermissionOnAndroid) {
        return this.hasPermissionOnAndroid;
      }

      if (this.platformService.isNativePlatform && this.isAndroid) {
        let hasLocationPermission: boolean = true;
        if (isAndroid10AndMore) {
          hasLocationPermission = await this.permissionService.checkAndRequestPermission(PermissionTypes.ACCESS_COARSE_LOCATION);
        }
        let hasBluetoothConnectPermission: boolean = true;
        let hasBluetoothScanPermission: boolean = true;
        if (isAndroid12AndMore) {
          hasBluetoothConnectPermission = await this.permissionService.checkAndRequestPermission(PermissionTypes.BLUETOOTH_CONNECT);
          hasBluetoothScanPermission = await this.permissionService.checkAndRequestPermission(PermissionTypes.BLUETOOTH_SCAN);
        }
        this.hasPermissionOnAndroid = hasLocationPermission && hasBluetoothConnectPermission && hasBluetoothScanPermission;
        return this.hasPermissionOnAndroid;
      }

      this.hasPermissionOnAndroid = true;
      return this.hasPermissionOnAndroid;
    } catch (error) {
      this.commonLogger.error(error, 'checkPermissionsOnAndroid');
      return false;
    }
  }

  public get discoverInProgressState(): Observable<boolean> {
    return this._discoverInProgress.asObservable();
  }

  protected logsSubscription(): void {
    document.addEventListener('deviceready', () => {
      this.callGetLogsMethod(({ message }) => {
        this.commonLogger.debug('pluginName:logsSubscription:message', { pluginName: this.pluginName, message });
      });
    });
  }

  protected callGetLogsMethod(onLogEvent: OnLogEvent) {
    window.cordova.exec(
      (message: string) => {
        onLogEvent({ message });
      },
      undefined,
      this.pluginName,
      'getLogs',
      []
    );
  }

  private printReceipt(receipt: any[], printer: PrinterDto): Promise<void> {
    const splitReceipts = this.getSplitReceipts(receipt, printer.deviceType);

    return splitReceipts.reduce((promise, splitReceipt) => {
      const fullReceipt = [].concat(...splitReceipt);
      return promise.then(() => this.callPrintReceiptMethod(printer, fullReceipt));
    }, Promise.resolve());
  }

  protected callPrintReceiptMethod(printer: PrinterDto, receipt: any[]): Promise<void> {
    return new Promise((resolve, reject) =>
      window.cordova.exec(resolve, reject, this.pluginName, this.printMethodName, [{ receipt, printer }])
    );
  }

  private getSplitReceipts(receipt: any[], printerType: PrinterType): any[][] {
    const splitValue = JSON.stringify({ cut: 'Nothing' });
    const length = receipt.length - 1;
    let splitReceipts: any = receipt.reduce((result: any[], value, index) => {
      let arr = result.pop() || [];
      arr = [...arr, value];
      if (JSON.stringify(value) === splitValue && index < length) {
        return [...result, arr, []];
      }
      return [...result, arr];
    }, []);

    const chunkSize = printerType === PrinterType.Star ? splitReceipts.length : 7;
    splitReceipts = [...Array(Math.ceil(splitReceipts.length / chunkSize))].map((_) => splitReceipts.splice(0, chunkSize));
    return splitReceipts;
  }
}
